import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Collapse from '@prism/collapse';
import 'react-use';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var AccordionContext = /*#__PURE__*/React.createContext();

function Accordion(_ref) {
  var children = _ref.children,
      onChange = _ref.onChange,
      collapseSiblings = _ref.collapseSiblings,
      open = _ref.open;

  var _React$useState = React.useState([]),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      openItem = _React$useState2[0],
      setOpenItem = _React$useState2[1];

  function getAccordionState() {
    return React.Children.map(children, function (child) {
      var childId = child.props.id;
      return {
        id: childId,
        open: openItem.includes(childId)
      };
    });
  }

  var ctx = {
    openItem: openItem,
    setOpenItem: setOpenItem,
    isItemOpen: function isItemOpen(id) {
      return openItem.includes(id);
    },
    toggleItem: function toggleItem(id) {
      // Conditionally check for single or array value presence of self.
      var isOpen = openItem.includes(id); // If collapseSiblings is true, add or remove this ID as appropriate.

      if (collapseSiblings) {
        var removeCurrent = openItem.filter(function (i) {
          return i !== id;
        });
        var addCurrent = [].concat(_toConsumableArray(openItem), [id]);
        setOpenItem(isOpen ? removeCurrent : addCurrent);
      } else {
        setOpenItem(openItem.includes(id) ? [] : [id]);
      }
    }
  }; // Inject an ID and our toggle function into children

  var childrenWithIdentifier = React.Children.map(children, function (child, idx) {
    return /*#__PURE__*/React.cloneElement(child, {
      id: child.props.id || "item-".concat(idx),
      toggle: ctx.toggleItem,
      defaultOpen: child.props.defaultOpen === true || open
    });
  }); // Fire our callbacks as provided

  React.useEffect(function () {
    onChange(getAccordionState());
  }, [openItem]);
  return /*#__PURE__*/React.createElement(AccordionContext.Provider, {
    value: ctx
  }, childrenWithIdentifier);
}

var propTypes$1 = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  open: PropTypes.bool,
  collapseSiblings: PropTypes.bool,
  onChange: PropTypes.func
};
var defaultProps = {
  children: null,
  collapseSiblings: true,
  open: false,
  onChange: function onChange() {}
};
Accordion.propTypes = propTypes$1;
Accordion.defaultProps = defaultProps;

var SelectableContext = /*#__PURE__*/React.createContext();

function AccordionItem(_ref) {
  var children = _ref.children,
      triggerClassName = _ref.triggerClassName,
      title = _ref.title,
      variant = _ref.variant,
      defaultOpen = _ref.defaultOpen,
      onClear = _ref.onClear,
      clearable = _ref.clearable,
      toggle = _ref.toggle,
      id = _ref.id;

  var _ref2 = useContext(SelectableContext) || {},
      toggleSelected = _ref2.toggleSelected;

  var _ref3 = useContext(AccordionContext) || {},
      openItem = _ref3.openItem,
      toggleItem = _ref3.toggleItem;

  var isFilter = variant === 'filter';

  var _React$useState = React.useState(defaultOpen),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      isOpen = _React$useState2[0],
      toggleOpen = _React$useState2[1]; // const isOpen = openItem.indexOf(id) >= 0;


  React.useEffect(function () {
    if (toggleOpen && openItem) {
      toggleOpen(openItem.indexOf(id) >= 0);
    }
  }, [openItem]);
  var clsx = {
    trigger: classNames(triggerClassName, 'accordion-trigger', isOpen && 'accordion-trigger--active', isFilter && 'accordion-trigger--filter'),
    caratDefault: classNames('accordion-trigger__carat', isOpen && 'accordion-trigger__carat--active', isFilter && 'accordion-trigger__carat--filter'),
    appendIcon: classNames(isFilter && clearable && 'icon prism-icon-cancel-circle accordion-filter__cancel-icon', !isFilter && 'icon prism-icon-chevron-down accordion-carat'),
    caratFilter: classNames(clearable && 'accordion-trigger__carat accordion-filter__cancel'),
    wrap: classNames('accordion-item__wrapper', isFilter && 'accordion-item__wrapper--filter')
  };
  useEffect(function () {
    if (defaultOpen) {
      toggleItem(id);
    }
  }, []);

  function handleClear(e) {
    onClear(e);
    toggleSelected(false);
  }

  return /*#__PURE__*/React.createElement("div", {
    id: id,
    className: clsx.wrap
  }, /*#__PURE__*/React.createElement("span", {
    role: "presentation",
    "aria-label": "Accordion trigger",
    className: clsx.trigger,
    onClick: function onClick() {
      return toggle(id);
    }
  }, isFilter && /*#__PURE__*/React.createElement("span", {
    className: clsx.caratDefault
  }, /*#__PURE__*/React.createElement("i", {
    className: "icon prism-icon-chevron-right-light accordion-carat"
  })), /*#__PURE__*/React.createElement("span", {
    className: "accordion-trigger-label"
  }, title), !!clsx.appendIcon && !isFilter && /*#__PURE__*/React.createElement("span", {
    role: "presentation",
    "aria-label": "Accordion Clear",
    onClick: handleClear,
    className: isFilter ? clsx.caratFilter : clsx.caratDefault
  }, /*#__PURE__*/React.createElement("i", {
    className: clsx.appendIcon
  }))), /*#__PURE__*/React.createElement(Collapse, {
    isOpen: isOpen
  }, children));
}

var propTypes = {
  // isOpen: PropTypes.bool,
  id: PropTypes.string,
  triggerClassName: PropTypes.string,
  children: PropTypes.node,
  defaultOpen: PropTypes.bool,
  title: PropTypes.string,
  variant: PropTypes.string,
  onClear: PropTypes.func,
  toggle: PropTypes.func,
  clearable: PropTypes.bool
};
AccordionItem.propTypes = propTypes;
AccordionItem.defaultProps = {
  id: undefined,
  children: null,
  defaultOpen: false,
  title: undefined,
  variant: undefined,
  onClear: function onClear() {},
  triggerClassName: undefined,
  toggle: undefined,
  clearable: false
};

function AccordionInput(_ref) {
  var id = _ref.id,
      children = _ref.children,
      defaultOpen = _ref.defaultOpen,
      onClear = _ref.onClear,
      title = _ref.title,
      name = _ref.name,
      onChange = _ref.onChange,
      clearable = _ref.clearable,
      onSubmit = _ref.onSubmit;

  function renderChildren() {
    return React.Children.map(children, function (child) {
      return /*#__PURE__*/React.cloneElement(child, {
        onChange: onChange
      });
    });
  }

  var formEl = useRef(null);
  var selectContext = useContext(SelectableContext);

  var _useContext = useContext(AccordionContext),
      setOpenItem = _useContext.setOpenItem,
      toggleItem = _useContext.toggleItem;

  var _ref2 = selectContext || false,
      ctxClear = _ref2.ctxClear;

  var handleClear = function handleClear(e) {
    onClear(e);

    if (e) {
      formEl.current.reset();
    }
  };

  useEffect(function () {
    renderChildren();

    if (defaultOpen) {
      setOpenItem([id]);
    }
  }, []);
  var accordionItemProps = {
    onClear: handleClear,
    onChange: onChange && onChange,
    defaultOpen: true,
    triggerTag: 'div',
    variant: 'filter',
    title: title,
    clearable: clearable,
    deselect: ctxClear,
    toggle: function toggle() {
      return toggleItem(id);
    },
    children: renderChildren(),
    id: id
  };
  var formProps = {
    id: id,
    onSubmit: onSubmit,
    name: name || title
  };
  return /*#__PURE__*/React.createElement("form", _extends({
    "aria-label": "Accordion form"
  }, formProps, {
    ref: formEl
  }), /*#__PURE__*/React.createElement(AccordionItem, accordionItemProps));
}

AccordionInput.propTypes = {
  id: PropTypes.string,
  children: PropTypes.node,
  onClear: PropTypes.func,
  defaultOpen: PropTypes.bool,
  title: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  clearable: PropTypes.bool,
  onSubmit: PropTypes.func
};
AccordionInput.defaultProps = {
  id: undefined,
  defaultOpen: true,
  onSubmit: function onSubmit(e) {
    e.preventDefault();
  },
  onClear: function onClear() {},
  onChange: function onChange() {},
  children: null,
  clearable: true,
  name: '',
  title: ''
};

Accordion.Input = AccordionInput;
Accordion.Item = AccordionItem;

export { AccordionInput, AccordionItem, Accordion as default };
//# sourceMappingURL=index.js.map
